import { useEffect, useId, useRef, useState, type PointerEvent } from "react";
import { gsap } from "gsap";

type TextHoverEffectProps = {
  text: string;
  duration?: number;
  className?: string;
};

export function TextHoverEffect({ text, duration = 0.18, className = "" }: TextHoverEffectProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const revealEllipseRef = useRef<SVGEllipseElement>(null);
  const animatedOutlineRef = useRef<SVGGElement>(null);
  const [hovered, setHovered] = useState(false);
  const id = useId().replace(/:/g, "");
  const gradientId = `text-gradient-${id}`;
  const revealGradientId = `text-reveal-gradient-${id}`;
  const revealMaskId = `text-reveal-mask-${id}`;
  const glyphId = `text-glyphs-${id}`;
  const outlineFilterId = `text-outline-filter-${id}`;
  const outlineMaskId = `text-outline-mask-${id}`;
  const glowFilterId = `text-glow-filter-${id}`;

  useEffect(() => {
    const svg = svgRef.current;
    const outline = animatedOutlineRef.current;
    if (!svg || !outline) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      gsap.set(outline, { opacity: 1 });
      return;
    }

    const context = gsap.context(() => {
      gsap.fromTo(
        outline,
        { opacity: 0.35 },
        { duration: 1.2, ease: "power3.out", opacity: 1 },
      );
    }, svg);

    return () => context.revert();
  }, []);

  const updateMask = (event: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    const revealEllipse = revealEllipseRef.current;
    if (!svg || !revealEllipse || event.pointerType === "touch") return;

    const rect = svg.getBoundingClientRect();
    const x = -12 + ((event.clientX - rect.left) / rect.width) * 324;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    gsap.to(revealEllipse, {
      attr: { cx: x, cy: y },
      duration,
      ease: "power2.out",
      overwrite: "auto",
    });
  };

  return (
    <svg
      aria-hidden="true"
      className={`text-hover-effect ${className}`.trim()}
      onPointerEnter={(event) => {
        if (event.pointerType !== "touch") {
          updateMask(event);
          setHovered(true);
        }
      }}
      onPointerLeave={() => setHovered(false)}
      onPointerMove={updateMask}
      preserveAspectRatio="xMidYMid meet"
      ref={svgRef}
      viewBox="-12 0 324 100"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient gradientUnits="userSpaceOnUse" id={gradientId} x1="-22" x2="322" y1="50" y2="50">
          <stop offset="0%" stopColor="#23d6ff" />
          <stop offset="52%" stopColor="#785cff" />
          <stop offset="100%" stopColor="#ff3d9a" />
        </linearGradient>

        <radialGradient cx="50%" cy="50%" id={revealGradientId} r="50%">
          <stop offset="0%" stopColor="white" stopOpacity="0.9" />
          <stop offset="12%" stopColor="white" stopOpacity="0.82" />
          <stop offset="30%" stopColor="white" stopOpacity="0.64" />
          <stop offset="48%" stopColor="white" stopOpacity="0.46" />
          <stop offset="66%" stopColor="white" stopOpacity="0.3" />
          <stop offset="80%" stopColor="white" stopOpacity="0.17" />
          <stop offset="90%" stopColor="white" stopOpacity="0.08" />
          <stop offset="96%" stopColor="white" stopOpacity="0.03" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>

        <mask
          height="1"
          id={revealMaskId}
          maskContentUnits="objectBoundingBox"
          maskUnits="objectBoundingBox"
          width="1"
          x="0"
          y="0"
        >
          <rect fill={`url(#${revealGradientId})`} height="1" width="1" />
        </mask>

        <text
          className="text-hover-effect__glyphs"
          dominantBaseline="middle"
          fill="white"
          id={glyphId}
          lengthAdjust="spacingAndGlyphs"
          textAnchor="middle"
          textLength="300"
          transform="translate(0 64) scale(1 1.3) translate(0 -64)"
          x="150"
          y="64"
        >
          {text}
        </text>

        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="140"
          id={outlineFilterId}
          width="348"
          x="-24"
          y="-20"
        >
          <feMorphology in="SourceAlpha" operator="dilate" radius="0.21" result="outer" />
          <feMorphology in="SourceAlpha" operator="erode" radius="0.21" result="inner" />
          <feComposite in="outer" in2="inner" operator="out" result="outlineAlpha" />
          <feFlood floodColor="white" result="outlineColor" />
          <feComposite in="outlineColor" in2="outlineAlpha" operator="in" />
        </filter>

        <mask height="136" id={outlineMaskId} maskUnits="userSpaceOnUse" width="344" x="-22" y="-18">
          <rect fill="black" height="136" width="344" x="-22" y="-18" />
          <use filter={`url(#${outlineFilterId})`} href={`#${glyphId}`} />
        </mask>

        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="148"
          id={glowFilterId}
          width="352"
          x="-26"
          y="-24"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.9" result="nearGlow" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.6" result="wideGlow" />
          <feComponentTransfer in="wideGlow" result="softWideGlow">
            <feFuncA slope="0.42" type="linear" />
          </feComponentTransfer>
          <feOffset dy="0.7" in="softWideGlow" result="offsetWideGlow" />
          <feMerge>
            <feMergeNode in="offsetWideGlow" />
            <feMergeNode in="nearGlow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g mask={`url(#${outlineMaskId})`} ref={animatedOutlineRef}>
        <rect className="text-hover-effect__outline-base" height="136" width="344" x="-22" y="-18" />
      </g>
      <g
        className="text-hover-effect__outline-signal"
        filter={`url(#${glowFilterId})`}
        opacity={hovered ? 1 : 0}
      >
        <g mask={`url(#${outlineMaskId})`}>
          <ellipse
            cx="150"
            cy="50"
            fill={`url(#${gradientId})`}
            mask={`url(#${revealMaskId})`}
            ref={revealEllipseRef}
            rx="130"
            ry="84.5"
          />
        </g>
      </g>
    </svg>
  );
}
